import React from 'react';

/**
 * Tracks whether the API lets the signed-in user act for an agent, from
 * every query result. Apps report results from the TanStack QueryCache;
 * pages render the noAgent screen when every request for the impersonated
 * agent has come back 403 and none has succeeded.
 *
 * The agent is found by looking for agent numbers (AAA######) anywhere in
 * the query key, which matches the `[path, { AgtNo }]` convention.
 */

const AGENT_NUMBER = /^[a-zA-Z]{3}\d{6}$/;

interface AccessCounts {
  forbidden: number;
  ok: number;
}

let access: Record<string, AccessCounts> = {};
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

/** Agent numbers mentioned anywhere in a query key */
const agentNumbersIn = (value: unknown, found = new Set<string>()) => {
  if (typeof value === 'string') {
    if (AGENT_NUMBER.test(value)) {
      found.add(value.toUpperCase());
    }
  } else if (Array.isArray(value)) {
    value.forEach((item) => agentNumbersIn(item, found));
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => agentNumbersIn(item, found));
  }
  return found;
};

/**
 * Report a finished query.
 *
 * @param queryKey - The query's key
 * @param error - The error, when it failed
 */
export const reportAgentQueryResult = (params: {
  queryKey: readonly unknown[];
  error?: unknown;
}) => {
  const status = (params.error as { response?: { status?: number } } | null)
    ?.response?.status;
  // Only a 403 says "not allowed"; other failures say nothing about access
  if (params.error && status !== 403) {
    return;
  }
  const agents = agentNumbersIn(params.queryKey);
  if (!agents.size) {
    return;
  }
  const next = { ...access };
  agents.forEach((agent) => {
    const counts = next[agent] || { forbidden: 0, ok: 0 };
    next[agent] = params.error
      ? { ...counts, forbidden: counts.forbidden + 1 }
      : { ...counts, ok: counts.ok + 1 };
  });
  access = next;
  emit();
};

/** Forget what we know about an agent (on start/stop impersonating) */
export const clearAgentAccess = (agentId?: string | null) => {
  if (!agentId || !access[agentId.toUpperCase()]) {
    return;
  }
  const next = { ...access };
  delete next[agentId.toUpperCase()];
  access = next;
  emit();
};

/**
 * True when the API has refused every request for this agent so far (at
 * least one 403, no successes).
 */
export function useAgentAccessDenied(agentId?: string | null) {
  const counts = React.useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => (agentId ? access[agentId.toUpperCase()] : undefined),
  );
  return Boolean(counts && counts.forbidden > 0 && counts.ok === 0);
}
